/**
 * Rosalie's contact with the farm.
 *
 * She is a small stuffed animal, so each impact has soft fabric mass behind a
 * slightly firmer felt tip. These must never turn into boots or hooves: the
 * player should hear a toy trying not to be heard.
 */

import { connectChain, envelope, filter, gain, noiseSource } from '../synth';
import type { SoundDefinition } from '../types';

/**
 * `wet` is beat 6's, and it is the surface half of rule 4 rather than the rain.
 *
 * `docs/beat-6-plan.md`: the yard is *"the one footfall in the game the player
 * will barely hear"*. Two different things make that true and they are kept
 * apart on purpose — this kind is **what she is standing on**, and the rain's
 * masking is a multiplier the yard applies at the emitter (`main.ts`), because
 * the weather is a fact about a beat and standing water is a fact about a floor.
 * Folding the rain in here would make every future wet surface quiet in the sun.
 */
export type SurfaceKind = 'concrete' | 'straw' | 'grating' | 'wet';

export type GaitKind = 'creep' | 'walk' | 'trot' | 'roll';

export const FOOTFALL_SOUND: Record<SurfaceKind, string> = {
  concrete: 'footfall-concrete',
  straw: 'footfall-straw',
  grating: 'footfall-grating',
  wet: 'yard-footfall',
};

/** Baseline for a trot on an ordinary farm floor, before surface and gait. */
export const BASE_EMISSION_RADIUS_METRES = 14;

/**
 * Surface changes both the sound and how much of it carries through the barn.
 * Straw absorbs the felt tip; grating turns it into an audible warning.
 */
export const SURFACE_EMISSION_MULTIPLIER: Record<SurfaceKind, number> = {
  concrete: 0.85,
  straw: 0.45,
  // Standing water on concrete is not quieter than concrete — a splash carries
  // slightly better than a dry click. It is the RAIN that hides her, and that
  // lives at the emitter. See `SurfaceKind`.
  wet: 0.95,
  grating: 1.35,
};

/** The stealth ladder from a near-silent creep to an exposed continuous roll. */
export const GAIT_EMISSION_MULTIPLIER: Record<GaitKind, number> = {
  creep: 0.2,
  walk: 0.6,
  trot: 1,
  roll: 1.8,
};

/**
 * Audible gameplay radius in metres for Rosalie's surface contact.
 *
 * A 14 m baseline makes straw-creep (1.26 m) nearly local, while grating-roll
 * (34.02 m) reaches almost the full 40 m grey-box room. The source gains follow
 * the same ordering: straw is quietest, concrete sits between it and the brighter
 * grating, so what players hear agrees with what can expose them.
 */
export function emissionRadius(surface: SurfaceKind, gait: GaitKind): number {
  return (
    BASE_EMISSION_RADIUS_METRES *
    SURFACE_EMISSION_MULTIPLIER[surface] *
    GAIT_EMISSION_MULTIPLIER[gait]
  );
}

const concreteFootfall: SoundDefinition = {
  id: 'footfall-concrete',
  label: 'Trotter on concrete',
  description: 'Dry felt thump with a small, damped concrete ring.',
  category: 'foley',
  duration: 0.18,
  loop: false,
  render(ctx) {
    const out = gain(ctx, 0.44);
    const body = envelope(ctx, 0.18, 0.004, 0.12);
    connectChain(
      noiseSource(ctx, 0.18, 131),
      filter(ctx, { type: 'bandpass', frequency: 520, q: 0.8 }),
      gain(ctx, 0.2),
      body,
      out,
      ctx.destination,
    );

    const ring = ctx.createOscillator();
    ring.type = 'triangle';
    ring.frequency.value = 690;
    connectChain(ring, gain(ctx, 0.052), envelope(ctx, 0.18, 0.003, 0.135), out);
    ring.start(0);
  },
};

const strawFootfall: SoundDefinition = {
  id: 'footfall-straw',
  label: 'Trotter on straw',
  description: 'A muffled felt landing with a brief, dry straw brush.',
  category: 'foley',
  duration: 0.22,
  loop: false,
  render(ctx) {
    const out = gain(ctx, 0.38);
    out.connect(ctx.destination);

    connectChain(
      noiseSource(ctx, 0.22, 157),
      filter(ctx, { type: 'lowpass', frequency: 780, q: 0.55 }),
      gain(ctx, 0.16),
      envelope(ctx, 0.22, 0.009, 0.145),
      out,
    );
    connectChain(
      noiseSource(ctx, 0.22, 163),
      filter(ctx, { type: 'highpass', frequency: 1700, q: 0.65 }),
      gain(ctx, 0.042),
      envelope(ctx, 0.22, 0.006, 0.175),
      out,
    );
  },
};

const gratingFootfall: SoundDefinition = {
  id: 'footfall-grating',
  label: 'Trotter on steel grating',
  description: 'Bright metal ping over a soft landing; the surface that gives her away.',
  category: 'foley',
  duration: 0.26,
  loop: false,
  render(ctx) {
    const out = gain(ctx, 0.55);
    out.connect(ctx.destination);

    connectChain(
      noiseSource(ctx, 0.26, 181),
      filter(ctx, { type: 'highpass', frequency: 1100, q: 0.7 }),
      filter(ctx, { type: 'lowpass', frequency: 6200, q: 0.7 }),
      gain(ctx, 0.16),
      envelope(ctx, 0.26, 0.004, 0.17),
      out,
    );

    const ping = ctx.createOscillator();
    ping.type = 'sine';
    ping.frequency.value = 1760;
    connectChain(ping, gain(ctx, 0.072), envelope(ctx, 0.26, 0.003, 0.205), out);
    ping.start(0);
  },
};

const landingThud: SoundDefinition = {
  id: 'landing-thud',
  label: 'Landing thud',
  description: 'Low, soft stuffed weight settling after a drop.',
  category: 'foley',
  duration: 0.35,
  loop: false,
  render(ctx) {
    const out = gain(ctx, 0.48);
    out.connect(ctx.destination);

    connectChain(
      noiseSource(ctx, 0.35, 197),
      filter(ctx, { type: 'lowpass', frequency: 340, q: 0.9 }),
      gain(ctx, 0.28),
      envelope(ctx, 0.35, 0.014, 0.24),
      out,
    );

    const weight = ctx.createOscillator();
    weight.type = 'triangle';
    weight.frequency.value = 76;
    connectChain(weight, gain(ctx, 0.13), envelope(ctx, 0.35, 0.012, 0.255), out);
    weight.start(0);
  },
};

/**
 * One revolution of a soft body going over concrete.
 *
 * **Rejected at review in its first form**, and the fault was in the brief I
 * wrote rather than in the synthesis: I asked for a flat, continuous,
 * unmodulated broadband loop so the caller could shift its rate freely. Flat
 * continuous broadband IS road noise, and Alex heard exactly that — "more like
 * car traffic in a busy city".
 *
 * The rebuild treats it as what it physically is: not a texture but a SERIES OF
 * SOFT IMPACTS. She is a stuffed body 0.55 m long, so each time she comes round,
 * a padded side meets the floor. Two contacts per revolution, because she is a
 * flattish rolling form rather than a ball, and near-silence between them.
 *
 * Nothing above ~900 Hz survives. That band was the tyres-on-tarmac component
 * and it is gone rather than merely reduced.
 *
 * **The loop period is ONE REVOLUTION and that is load-bearing.** `GameAudio`
 * plays it at her real revolutions per second, so the impacts land under the
 * rotation the player can see. Change this duration and the sound drifts against
 * the picture — the same failure the footfalls are built to avoid.
 */
const rollRumble: SoundDefinition = {
  id: 'roll-rumble',
  label: 'Rolling rumble',
  description: 'One revolution: two soft body-contacts of a tucked plush pig on concrete.',
  category: 'foley',
  duration: 0.8,
  loop: true,
  render(ctx) {
    const out = gain(ctx, 0.62);
    out.connect(ctx.destination);
    const t0 = ctx.currentTime;

    // Two contacts, half a revolution apart. The second is slightly lighter and
    // slightly later than exactly half — a real body is not balanced, and perfect
    // symmetry is audible as a machine.
    const contacts: readonly [number, number][] = [
      [0.02, 1],
      [0.42, 0.82],
    ];

    for (const [offset, strength] of contacts) {
      // The body: low, soft, quick to fall away. No transient snap — a click here
      // would read as something hard, and nothing about her is hard.
      const bodyShape = gain(ctx, 0);
      bodyShape.gain.setValueAtTime(0, t0 + offset);
      bodyShape.gain.linearRampToValueAtTime(0.5 * strength, t0 + offset + 0.028);
      bodyShape.gain.exponentialRampToValueAtTime(0.0008, t0 + offset + 0.3);
      connectChain(
        noiseSource(ctx, 0.8, 211 + Math.round(offset * 100)),
        filter(ctx, { type: 'lowpass', frequency: 300, q: 0.8 }),
        bodyShape,
        out,
      );

      // The mass underneath it, which is what makes it read as weight.
      const weight = ctx.createOscillator();
      weight.type = 'sine';
      weight.frequency.value = 62;
      const weightShape = gain(ctx, 0);
      weightShape.gain.setValueAtTime(0, t0 + offset);
      weightShape.gain.linearRampToValueAtTime(0.2 * strength, t0 + offset + 0.022);
      weightShape.gain.exponentialRampToValueAtTime(0.0008, t0 + offset + 0.26);
      connectChain(weight, weightShape, out);
      weight.start(0);

      // A brush of fabric dragging away from the contact. Band-limited well below
      // the range that made the old version sound like a road surface.
      const brushShape = gain(ctx, 0);
      brushShape.gain.setValueAtTime(0, t0 + offset + 0.01);
      brushShape.gain.linearRampToValueAtTime(0.07 * strength, t0 + offset + 0.06);
      brushShape.gain.linearRampToValueAtTime(0, t0 + offset + 0.19);
      connectChain(
        noiseSource(ctx, 0.8, 307 + Math.round(offset * 100)),
        filter(ctx, { type: 'bandpass', frequency: 520, q: 0.9 }),
        filter(ctx, { type: 'lowpass', frequency: 900, q: 0.7 }),
        brushShape,
        out,
      );
    }

    // A very quiet floor tone under the whole revolution, so the gaps between
    // contacts are near-silent rather than actually silent — a hole in a loop is
    // as audible as a click.
    connectChain(
      noiseSource(ctx, 0.8, 223),
      filter(ctx, { type: 'lowpass', frequency: 190, q: 0.7 }),
      gain(ctx, 0.05),
      out,
    );
  },
};

const scrambleScrabble: SoundDefinition = {
  id: 'scramble-scrabble',
  label: 'Scramble up',
  description: 'Uneven fabric scuffs on concrete, ending in a small settle.',
  category: 'foley',
  duration: 0.5,
  loop: false,
  render(ctx) {
    const out = gain(ctx, 0.46);
    const shape = gain(ctx, 0);
    const t0 = ctx.currentTime;
    const scuffs: readonly [number, number, number][] = [
      [0.018, 0.09, 0.036],
      [0.092, 0.14, 0.044],
      [0.176, 0.105, 0.029],
      [0.238, 0.17, 0.052],
      [0.334, 0.075, 0.065],
    ];

    for (const [offset, level, decay] of scuffs) {
      shape.gain.setValueAtTime(0, t0 + offset);
      shape.gain.linearRampToValueAtTime(level, t0 + offset + 0.006);
      shape.gain.linearRampToValueAtTime(0, t0 + offset + decay);
    }

    connectChain(
      noiseSource(ctx, 0.5, 239),
      filter(ctx, { type: 'bandpass', frequency: 1250, q: 0.9 }),
      shape,
      envelope(ctx, 0.5, 0.003, 0.12),
      out,
      ctx.destination,
    );
  },
};

export const foleySounds: readonly SoundDefinition[] = [
  concreteFootfall,
  strawFootfall,
  gratingFootfall,
  landingThud,
  rollRumble,
  scrambleScrabble,
];
